import React, { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { DECLINE_REASONS } from "@/pages/hub/constants";
import { Btn, Fld, Inp } from "@/components/portal/crm/kit";
import { Calendar } from "@/components/ui/calendar";
import { Modal } from "./Modal";

/** A UK-format date field (e.g. Fri 30 Oct 2026) that opens a calendar in place; value is YYYY-MM-DD. */
export function UkDate({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const day = value ? parseISO(value) : undefined;
  return (
    <div>
      <button type="button" className="inp" onClick={() => setOpen((o) => !o)} data-testid={testId}
              style={{ textAlign: "left", cursor: "pointer", color: day ? undefined : "var(--mut)" }}>
        {day ? format(day, "EEE d MMM yyyy") : "Pick a date"}
      </button>
      {open && (
        <div style={{ marginTop: 6, border: "1px solid var(--line)", borderRadius: 12, background: "#fff", display: "inline-block" }}
             data-testid={`${testId}-calendar`}>
          <Calendar mode="single" selected={day} weekStartsOn={1} disabled={{ before: new Date() }}
                    onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }} />
        </div>
      )}
    </div>
  );
}

/**
 * One dialog for the two stage changes that need something typed: a decline needs a reason,
 * and an offer needs the terms so the introducer can put them side by side with the other
 * desks instead of waiting for a phone call. Everything on the offer form is optional — an
 * offer with no figures is still an offer.
 */
export function DeskDecision({ open, mode, onClose, onConfirm }) {
  const [reason, setReason] = useState(DECLINE_REASONS[0]);
  const [notes, setNotes] = useState("");
  const [rate, setRate] = useState("");
  const [term, setTerm] = useState("");
  const [fee, setFee] = useState("");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason(DECLINE_REASONS[0]); setNotes(""); setRate(""); setTerm(""); setFee(""); setExpires("");
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "declined") {
        await onConfirm({ decline_reason: reason, notes: notes || null });
      } else {
        await onConfirm({
          offer_rate_pct: rate === "" ? null : Number(rate),
          offer_term_months: term === "" ? null : Number(term),
          offer_fee_gbp: fee === "" ? null : Number(fee),
          offer_expires_on: expires || null,
          offer_notes: notes || null,
        });
      }
    } finally { setBusy(false); }
  };

  const declining = mode === "declined";

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={declining ? 560 : 620}
      title={declining ? "Why are you passing?" : "Your terms on this case"}
      sub={declining
        ? "One line is enough. It stays between you and the Connexis desk, and it is what sharpens what we send you next."
        : "These go straight onto the introducer's case so the client can compare like for like. Leave anything you have not settled blank."}
      testId={declining ? "lender-decline-dialog" : "lender-offer-dialog"}
      footer={
        <>
          <Btn onClick={onClose} testId="desk-decision-cancel">Not yet</Btn>
          <Btn kind={declining ? "ink" : "primary"} onClick={submit} disabled={busy} testId="desk-decision-confirm">
            {busy ? "Saving…" : declining ? "Pass on this case" : "Send my terms"}
          </Btn>
        </>
      }
    >
      {declining ? (
        <>
          <Fld label="Reason">
            <div className="chips">
              {DECLINE_REASONS.map((r) => (
                <button key={r} type="button" className={`chip${reason === r ? " on" : ""}`} onClick={() => setReason(r)}
                        data-testid={`lender-decline-chip-${r.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  {r}
                </button>
              ))}
            </div>
          </Fld>
          <Fld label="Anything else (optional)">
            <textarea className="inp" value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Any extra context…" data-testid="lender-decline-notes" />
          </Fld>
        </>
      ) : (
        <>
          <div className="grid2">
            <Fld label="Rate %"><Inp value={rate} onChange={(e) => setRate(e.target.value)} inputMode="decimal"
                                     placeholder="11.4" data-testid="lender-offer-rate" /></Fld>
            <Fld label="Term in months"><Inp value={term} onChange={(e) => setTerm(e.target.value)} inputMode="numeric"
                                              placeholder="60" data-testid="lender-offer-term" /></Fld>
            <Fld label="Arrangement fee £"><Inp value={fee} onChange={(e) => setFee(e.target.value)} inputMode="numeric"
                                                 placeholder="1200" data-testid="lender-offer-fee" /></Fld>
            <Fld label="Valid until"><UkDate value={expires} onChange={setExpires} testId="lender-offer-expires" /></Fld>
          </div>
          <Fld label="A line for the introducer (optional)">
            <textarea className="inp" value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Happy to proceed on the schedule as submitted. If the supplier invoice moves by more than 5% we would need to re-quote."
                      data-testid="lender-offer-notes" />
          </Fld>
          <p style={{ color: "var(--mut)", fontSize: 12.5, margin: 0 }}>
            Your desk is never named. The introducer sees these terms against a letter — Desk A, Desk B — and the
            letters mean nothing on the next case.
          </p>
        </>
      )}
    </Modal>
  );
}

export default DeskDecision;
